<template>
  <modal :title="formTitle" :forceShowSave="true" @close="$emit('close')" @save="onSave">
    <template #body>
      <VueTabs>
        <!-- Tab 1: General Information -->
        <v-tab title="General Information">
          <div class="md-layout tunnels-form">
            <div
              v-for="col in generalFields"
              :key="col.field"
              class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33"
            >
              <md-field :class="{ 'md-invalid': col.field === 'completion_year' && !isYearValid && formData[col.field] }">
                <label>{{ col.label }}</label>
                <md-input 
                  v-model="formData[col.field]" 
                  :type="col.type"
                  :min="col.min"
                  :max="col.max"
                  :step="col.step"
                  :maxlength="col.maxlength"
                />
                <span v-if="col.field === 'completion_year' && formData[col.field] && yearErrorMessage" class="md-error">{{ yearErrorMessage }}</span>
              </md-field>
            </div>
          </div>
        </v-tab>

        <!-- Tab 2: Basic Dimensions -->
        <v-tab title="Basic Dimensions">
          <div class="md-layout tunnels-form">
            <div
              v-for="col in basicDimensionsFields"
              :key="col.field"
              :class="[
                'md-layout-item md-small-size-100 md-medium-size-50',
                (col.field === 'tunnel_support_structure_en' || col.field === 'tunnel_support_structure_ru') 
                  ? 'wide-field' 
                  : 'md-size-33'
              ]"
            >
              <md-field>
                <label>{{ col.label }}</label>
                <md-input 
                  v-model="formData[col.field]" 
                  :type="col.type"
                  :min="col.min"
                  :max="col.max"
                  :step="col.step"
                  :maxlength="col.maxlength"
                />
              </md-field>
            </div>
          </div>
        </v-tab>

        <!-- Tab 3: Pavement -->
        <v-tab title="Pavement">
          <div class="md-layout tunnels-form">
            <div
              v-for="col in pavementFields"
              :key="col.field"
              class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33"
            >
              <md-field>
                <label>{{ col.label }}</label>
                <md-input 
                  v-model="formData[col.field]" 
                  :type="col.type"
                  :min="col.min"
                  :max="col.max"
                  :step="col.step"
                  :maxlength="col.maxlength"
                />
              </md-field>
            </div>
          </div>
        </v-tab>

        <!-- Tab 4: Ventilation -->
        <v-tab title="Ventilation">
          <div class="md-layout tunnels-form">
            <div
              v-for="col in ventilationFields"
              :key="col.field"
              class="md-layout-item md-small-size-100 md-medium-size-50 wide-field"
            >
              <md-field>
                <label>{{ col.label }}</label>
                <md-input 
                  v-model="formData[col.field]" 
                  :type="col.type"
                  :min="col.min"
                  :max="col.max"
                  :step="col.step"
                  :maxlength="col.maxlength"
                />
              </md-field>
            </div>
          </div>
        </v-tab>

        <!-- Tab 5: Facilities -->
        <v-tab title="Facilities">
          <div class="md-layout tunnels-form">
            <div
              v-for="col in facilitiesFields"
              :key="col.field"
              :class="[
                'md-layout-item md-small-size-100 md-medium-size-50',
                (col.field === 'facilities1' || col.field === 'facilities2' || col.field === 'facilities3') 
                  ? 'wide-field' 
                  : 'md-size-33'
              ]"
            >
              <md-field :class="{ 
                'md-invalid': (col.field === 'latitude_n' && formData[col.field] && !isLatitudeValid) ||
                              (col.field === 'longitude_e' && formData[col.field] && !isLongitudeValid)
              }">
                <label>{{ col.label }}</label>
                <md-input 
                  v-model="formData[col.field]" 
                  :type="col.type"
                  :min="col.min"
                  :max="col.max"
                  :step="col.step"
                  :maxlength="col.maxlength"
                />
                <span v-if="col.field === 'latitude_n' && formData[col.field] && latitudeErrorMessage" class="md-error">{{ latitudeErrorMessage }}</span>
                <span v-if="col.field === 'longitude_e' && formData[col.field] && longitudeErrorMessage" class="md-error">{{ longitudeErrorMessage }}</span>
              </md-field>
            </div>
          </div>
        </v-tab>
      </VueTabs>
    </template>
  </modal>
</template>

<script>
import { VueTabs, VTab } from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css'
import { Modal } from '@/pages/Components'
import { savedMessage, errorMessage } from '@/mixins/messagesMixin'

export default {
  name: 'TunnelsTableEditForm',
  components: { VueTabs, VTab, Modal },
  props: {
    tunnel: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      formData: { ...this.tunnel },
      // General Information fields
      generalFields: [
        { field: "tunnel_name_en", label: "Name", type: "text", maxlength: 100 },
        { field: "tunnel_name_ru", label: "Name (ru):", type: "text", maxlength: 100 },
        { field: "road_name", label: "Road name", type: "text", maxlength: 100 },
        { field: "reference_number", label: "Reference number:", type: "number", step: 1, min: 0 },
        { field: "tunnel_length", label: "Length", type: "number", min: 0, step: 1 },
        { field: "distance_post", label: "Distance post", type: "number", min: 0, step: 1 },
        { field: "completion_year", label: "Completion year", type: "number", min: 1900, max: 2100, step: 1, maxlength: 4 },
        { field: "competent_dep", label: "Competent DEU", type: "text", maxlength: 100 }
      ],
      // Basic Dimensions fields
      basicDimensionsFields: [
        { field: "traffic_volume", label: "Traffic volume", type: "number", min: 0, step: 1 },
        { field: "regulation_speed", label: "Regulation speed", type: "number", min: 0, step: 1 },
        { field: "traffic_form_en", label: "Traffic form (en)", type: "text", maxlength: 100 },
        { field: "traffic_form_ru", label: "Traffic form (ru)", type: "text", maxlength: 100 },
        { field: "longitudinal_slope", label: "Longitudinal slope", type: "text", maxlength: 100 },
        { field: "alignment", label: "Alignment", type: "text", maxlength: 100 },
        { field: "tunnel_support_structure_en", label: "Tunnel support structure (en)", type: "text", maxlength: 100 },
        { field: "tunnel_support_structure_ru", label: "Tunnel support structure (ru)", type: "text", maxlength: 100 }
      ],
      // Pavement section fields
      pavementFields: [
        { field: "pavement_type_en", label: "Pavement type (en)", type: "text", maxlength: 100 },
        { field: "pavement_type_ru", label: "Pavement type (ru)", type: "text", maxlength: 100 },
        { field: "pavement_thickness", label: "Pavement thickness:", type: "text", maxlength: 100 },
        { field: "lighting_type_specification", label: "Lighting type specification", type: "text", maxlength: 100 },
        { field: "lighting_number", label: "Lighting number", type: "number", min: 0, step: 1 }
      ],
      // Ventilation section fields
      ventilationFields: [
        { field: "ventilation_type_en", label: "Ventilation type (en)", type: "text", maxlength: 100 },
        { field: "ventilation_type_ru", label: "Ventilation type (ru)", type: "text", maxlength: 100 },
        { field: "ventilation_specification_number", label: "Ventilation specification number", type: "text", maxlength: 100 }
      ],
      // Facilities section fields
      facilitiesFields: [
        { field: "facilities1", label: "Facilities 1", type: "text", maxlength: 100 },
        { field: "facilities2", label: "Facilities 2", type: "text", maxlength: 100 },
        { field: "facilities3", label: "Facilities 3", type: "text", maxlength: 100 },
        { field: "receiving_voltage", label: "Receiving voltage", type: "text", maxlength: 100 },
        { field: "cross_section", label: "Cross section", type: "text", maxlength: 100 },
        { field: "width_of_road_bishkek_side", label: "Width of road Bishkek side", type: "text", maxlength: 100 },
        { field: "width_of_road_osh_side", label: "Width of road Osh side", type: "text", maxlength: 100 },
        { field: "longitude_e", label: "Longitude", type: "number", step: "any" },
        { field: "latitude_n", label: "Latitude", type: "number", step: "any" }
      ]
    }
  },
  computed: {
    formTitle() {
      return this.tunnel && this.tunnel.tunnel_uuid
        ? this.$t('screen_titles.tunnel_upd')
        : this.$t('screen_titles.tunnel_add')
    },
    
    isYearValid() {
      const year = this.formData.completion_year;
      if (!year) return true; // Optional field
      const yearStr = year.toString();
      const yearNum = parseInt(year);
      
      // Must be exactly 4 digits and within valid range
      return yearStr.length === 4 && yearNum >= 1900 && yearNum <= 2100 && !isNaN(yearNum);
    },
    
    yearErrorMessage() {
      if (!this.formData.completion_year) return '';
      if (!this.isYearValid) {
        const year = this.formData.completion_year.toString();
        if (year.length !== 4) {
          return 'Year must be exactly 4 digits';
        } else {
          return 'Year must be between 1900 and 2100';
        }
      }
      return '';
    },
    
    isLatitudeValid() {
      const lat = this.formData.latitude_n;
      if (!lat && lat !== 0) return true; // optional field
      const latNum = parseFloat(lat);
      return !isNaN(latNum) && latNum >= -90 && latNum <= 90;
    },
    
    latitudeErrorMessage() {
      if (!this.formData.latitude_n && this.formData.latitude_n !== 0) return '';
      if (!this.isLatitudeValid) {
        return 'Latitude must be between -90 and 90 degrees';
      }
      return '';
    },
    
    isLongitudeValid() {
      const lng = this.formData.longitude_e;
      if (!lng && lng !== 0) return true; // optional field
      const lngNum = parseFloat(lng);
      return !isNaN(lngNum) && lngNum >= -180 && lngNum <= 180;
    },
    
    longitudeErrorMessage() {
      if (!this.formData.longitude_e && this.formData.longitude_e !== 0) return '';
      if (!this.isLongitudeValid) {
        return 'Longitude must be between -180 and 180 degrees';
      }
      return '';
    }
  },
  watch: {
    'formData.completion_year'(newValue) {
      if (newValue) {
        let cleanValue = newValue.toString().replace(/\D/g, '').substring(0, 4);
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.formData.completion_year = cleanValue;
          });
        }
      }
    },
    'formData.latitude_n'(newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        // Allow decimal numbers with optional minus sign
        let cleanValue = newValue.toString().replace(/[^-0-9.]/g, '');
        // Ensure only one decimal point and one minus sign at start
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        if (cleanValue.indexOf('-') > 0) {
          cleanValue = cleanValue.replace(/-/g, '');
          if (newValue.toString().startsWith('-')) {
            cleanValue = '-' + cleanValue;
          }
        }
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.formData.latitude_n = cleanValue;
          });
        }
      }
    },
    'formData.longitude_e'(newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        // Allow decimal numbers with optional minus sign
        let cleanValue = newValue.toString().replace(/[^-0-9.]/g, '');
        // Ensure only one decimal point and one minus sign at start
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        if (cleanValue.indexOf('-') > 0) {
          cleanValue = cleanValue.replace(/-/g, '');
          if (newValue.toString().startsWith('-')) {
            cleanValue = '-' + cleanValue;
          }
        }
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.formData.longitude_e = cleanValue;
          });
        }
      }
    }
  },

  methods: {

    async onSave() {
      try {
        // Validate year before saving
        if (this.formData.completion_year && !this.isYearValid) {
          await errorMessage(this.yearErrorMessage || 'Please enter a valid year (1900-2100)');
          return;
        }
        
        // Validate latitude before saving
        if ((this.formData.latitude_n || this.formData.latitude_n === 0) && !this.isLatitudeValid) {
          await errorMessage(this.latitudeErrorMessage || 'Please enter a valid latitude (-90 to 90)');
          return;
        }
        
        // Validate longitude before saving
        if ((this.formData.longitude_e || this.formData.longitude_e === 0) && !this.isLongitudeValid) {
          await errorMessage(this.longitudeErrorMessage || 'Please enter a valid longitude (-180 to 180)');
          return;
        }

        // Convert numeric fields to numbers
        const payload = { ...this.formData };
        
        // Convert string values to numbers for numeric fields (same approach as bridges)
        const numericFields = [
          'tunnel_length', 'distance_post', 'completion_year', 'traffic_volume', 
          'regulation_speed', 'lighting_number', 'longitude_e', 'latitude_n', 'reference_number'
        ];
        
        numericFields.forEach(field => {
          if (payload[field] !== null && payload[field] !== undefined && payload[field] !== '') {
            const numValue = Number(payload[field]);
            payload[field] = isNaN(numValue) ? 0 : numValue;
          } else {
            // Use default value 0 for empty numeric fields (same as bridges approach)
            payload[field] = 0;
          }
        });

        // Convert empty strings to empty string defaults (same as bridges approach)
        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === undefined) {
            // Set default values: 0 for numeric fields, empty string for text fields
            if (numericFields.includes(key)) {
              payload[key] = 0;
            } else {
              payload[key] = '';
            }
          }
        });

        // For new tunnels, remove tunnel_uuid (should be auto-generated)
        if (!this.tunnel || !this.tunnel.tunnel_uuid) {
          delete payload.tunnel_uuid;
        }

        if (this.tunnel && this.tunnel.tunnel_uuid) {
          // Edit existing tunnel
          await this.$store.dispatch('UPDATE_TUNNEL', {
            id: this.tunnel.tunnel_uuid,
            theItem: payload
          });
        } else {
          // Add new tunnel
          await this.$store.dispatch('ADD_NEW_TUNNEL', payload);
        }
        
        // Notify parent to refresh table
        this.$emit('saved', payload);
        // Close modal only on success
        this.$emit('close');
        // Show success message using the same pattern as bridges
        await savedMessage(null, this.$t('map.tunnels'), payload.tunnel_name_ru || payload.tunnel_name_en);
      } catch (error) {
        console.error('Error saving tunnel:', error);
        // On error keep the modal open and show an error dialog
        await errorMessage(error.message || this.$t('errors.unknown_error'));
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Compact layout for tunnels form - three columns with minimal padding
.tunnels-form {
  .md-layout-item {
    padding: 2px 4px !important; // Minimal padding for tight row spacing
    margin-bottom: 0px !important; // No extra margin between items
  }
  
  // Special styling for wider fields (support structure fields)
  .md-layout-item.wide-field {
    flex: 0 0 100% !important; // Full width for support structure fields
    max-width: 100% !important;
  }
}

// Fix character counter with proper spacing
:deep(.md-field) {
  margin-bottom: 20px !important; // Adequate space for counter
  
  .md-counter {
    bottom: -18px !important; // Position counter below input line
    right: 0 !important;
    font-size: 12px !important;
    color: #999 !important;
  }
  
  // Ensure input has proper padding on right for counter
  .md-input {
    padding-right: 60px !important; // Space for character counter
  }
}

// Ensure proper spacing in tabs
:deep(.vue-tabs__content) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

// Responsive adjustments
@media (max-width: 1200px) {
  .tunnels-form {
    .md-layout-item {
      flex: 50% !important;
      max-width: 50% !important;
    }
    
    .md-layout-item.wide-field {
      flex: 50% !important;
      max-width: 50% !important;
    }
  }
}

@media (max-width: 768px) {
  .tunnels-form {
    .md-layout-item {
      flex: 100% !important;
      max-width: 100% !important;
    }
    
    .md-layout-item.wide-field {
      flex: 100% !important;
      max-width: 100% !important;
    }
  }
}

// Validation error styling (year, latitude, longitude)
:deep(.md-field.md-invalid) {
  .md-input {
    border-bottom-color: #f44336 !important;
  }
  
  .md-error {
    color: #f44336 !important;
    font-size: 12px !important;
    margin-top: 4px !important;
    display: block !important;
  }
}
</style>